import { ApiError } from "../apiClient";
import { DOMAIN_ERROR_CODES } from "../errors/domainCodes";
import { DomainError } from "../errors/DomainError";
import { NetworkError } from "../errors/NetworkError";
import { SupabaseRequestError } from "../errors/SupabaseRequestError";
import { RETRYABLE_RESPONSE_STATUS_SET } from "../http/retryableStatuses";

import type { ErrorPolicyOutcome, SagaOperationContext } from "./types";
const CLIENT_HTTP = new Set([400, 404, 409, 422]);
const AUTH_HTTP = new Set([401, 403]);

function isExpectedHttpStatus(context: SagaOperationContext, status: number): boolean {
  const list = context.expectedHttpStatuses;
  return list !== undefined && list.length > 0 && list.includes(status);
}

function withDefaults(context: SagaOperationContext): SagaOperationContext & {
  critical: boolean;
  notifyUser: boolean;
} {
  return {
    ...context,
    critical: context.critical !== false,
    notifyUser: context.notifyUser !== false,
  };
}

function transientTelemetry(ctx: { critical: boolean }): Pick<ErrorPolicyOutcome, "sentryEvent" | "severity"> {
  if (ctx.critical) {
    return { sentryEvent: "exception", severity: "warning" };
  }
  return { sentryEvent: "message", severity: "warning" };
}

/**
 * Pure policy: maps an unknown error + operation context to Sentry/UX hints.
 * Does not call Sentry or dispatch Redux actions.
 */
export function resolveErrorPolicy(
  error: unknown,
  context: SagaOperationContext,
): ErrorPolicyOutcome {
  const ctx = withDefaults(context);

  if (error instanceof DomainError) {
    if (error.code === DOMAIN_ERROR_CODES.AUTH_INVALID_CREDENTIALS) {
      return {
        severity: "info",
        sentryEvent: "none",
        notifyUser: ctx.notifyUser,
        reason: "auth_invalid_credentials",
      };
    }
    return {
      severity: "info",
      sentryEvent: "message",
      notifyUser: ctx.notifyUser,
      reason: "domain_error",
    };
  }

  if (error instanceof NetworkError) {
    const { sentryEvent, severity } = transientTelemetry(ctx);
    return {
      severity,
      sentryEvent,
      notifyUser: ctx.notifyUser,
      reason: "network_error",
    };
  }

  if (error instanceof SupabaseRequestError) {
    const { sentryEvent, severity } = transientTelemetry(ctx);
    return {
      severity,
      sentryEvent,
      notifyUser: ctx.notifyUser,
      reason: "supabase_request_error",
    };
  }

  if (error instanceof ApiError) {
    const status = error.status;
    if (RETRYABLE_RESPONSE_STATUS_SET.has(status)) {
      const { sentryEvent, severity } = transientTelemetry(ctx);
      return {
        severity,
        sentryEvent,
        notifyUser: ctx.notifyUser,
        reason: "transient_http",
      };
    }

    const isExpected = isExpectedHttpStatus(ctx, status);

    if (AUTH_HTTP.has(status) && !isExpected) {
      return {
        severity: "warning",
        sentryEvent: "message",
        notifyUser: ctx.notifyUser,
        reason: "auth_http",
      };
    }

    if (CLIENT_HTTP.has(status) || isExpected) {
      return {
        severity: "info",
        sentryEvent: "message",
        notifyUser: ctx.notifyUser,
        reason: isExpected ? "expected_http" : "client_http",
      };
    }

    if (status >= 500) {
      const { sentryEvent, severity } = transientTelemetry(ctx);
      return {
        severity,
        sentryEvent,
        notifyUser: ctx.notifyUser,
        reason: "server_http",
      };
    }

    return {
      severity: "info",
      sentryEvent: "message",
      notifyUser: ctx.notifyUser,
      reason: "http_other",
    };
  }

  if (error instanceof TypeError || error instanceof ReferenceError || error instanceof SyntaxError) {
    return {
      severity: "error",
      sentryEvent: "exception",
      notifyUser: ctx.notifyUser,
      reason: "programming_error",
    };
  }

  if (error instanceof Error) {
    return {
      severity: "error",
      sentryEvent: "exception",
      notifyUser: ctx.notifyUser,
      reason: "generic_error",
    };
  }

  if (typeof error === "string") {
    return {
      severity: "error",
      sentryEvent: "message",
      notifyUser: ctx.notifyUser,
      reason: "string_error",
    };
  }

  return {
    severity: "error",
    sentryEvent: "exception",
    notifyUser: ctx.notifyUser,
    reason: "unknown_error",
  };
}
